import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined'
import { Box, Card, Divider, Typography } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { useCreativeAdDetails } from '../../hooks/useCreativeAdDetails'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { CreativeAdDetailsSkeleton } from '../CreativeAdDetailsSkeleton/CreativeAdDetailsSkeleton'
import { CreativeAdInfoStrip } from '../CreativeAdInfoStrip/CreativeAdInfoStrip'
import { CreativeAdTagDimensionList } from '../CreativeAdTagDimensionList/CreativeAdTagDimensionList'
import { CreativeAdVideoPlayer, CreativeAdVideoPlayerHandle } from '../CreativeAdVideoPlayer/CreativeAdVideoPlayer'
import { MotivationTimeline } from '../MotivationTimeline/MotivationTimeline'
import { SecondDetailPanel } from '../SecondDetailPanel/SecondDetailPanel'
import { SectionCard } from '../SectionCard/SectionCard'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'

export const CreativeAdDetailsDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack } = useUAInsightsNavigation()
  const open = params.dialog === 'ad'
  const creativeId = open ? params.creativeId : null
  const { data, isLoading, isError, refetch } = useCreativeAdDetails(creativeId)
  // Anchored to the game's strongest creative in scope, the same denominator
  // the game's listing uses, so the percentage matches the card the user
  // clicked from.
  const adPerformancePct = data && data.gameMaxInstallShare > 0 ? Math.min(100, ((data.installShare ?? 0) / data.gameMaxInstallShare) * 100) : null

  const videoPlayerRef = useRef<CreativeAdVideoPlayerHandle>(null)
  const [currentSecond, setCurrentSecond] = useState<number | null>(null)

  const handleClose = useCallback(() => {
    videoPlayerRef.current?.reset()
    close()
  }, [close])

  const handleSeekToSecond = useCallback((second: number) => {
    videoPlayerRef.current?.seekTo(second)
  }, [])

  const timelineSeconds = useMemo(() => data?.seconds ?? [], [data?.seconds])

  const activeSecondData = useMemo(() => {
    if (currentSecond == null) return null
    return timelineSeconds.find((s) => s.second === currentSecond) ?? null
  }, [currentSecond, timelineSeconds])

  if (!open) return null

  return (
    <UAInsightsDialog
      open={open}
      onClose={handleClose}
      title={t('ad_details')}
      canGoBack={canGoBack}
      onGoBack={goBack}
      fullScreen
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      loadingWhenNoData
      loadingPlaceholder={<CreativeAdDetailsSkeleton />}
    >
      {data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card variant="outlined">
            <CreativeAdInfoStrip data={data} adPerformancePct={adPerformancePct} />
            <Divider />
            <Box
              sx={{
                display: 'grid',
                // `minmax(0, 1fr)` (instead of plain `1fr`) prevents either
                // column's intrinsic min-content — the timeline's series
                // SVG on the right, the video player's transport controls
                // on the left — from pushing the column past the equal
                // 50/50 share, so the video gets as much room as the
                // timeline column.
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
                gap: 2,
                p: 2,
              }}
            >
              <CreativeAdVideoPlayer
                ref={videoPlayerRef}
                videoUrl={data.videoUrl}
                posterUrl={data.thumbUrl}
                videoDuration={data.videoDurationSeconds}
                onCurrentSecondChange={setCurrentSecond}
              />

              <Divider orientation="vertical" flexItem />

              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('motivation_timeline')}
                  </Typography>
                  <MotivationTimeline seconds={timelineSeconds} currentSecond={currentSecond} onSecondClick={handleSeekToSecond} />
                </Box>

                {activeSecondData ? (
                  <SecondDetailPanel secondData={activeSecondData} />
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: 'action.hover',
                      borderRadius: borderRadiusDefault,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      minHeight: 80,
                    }}
                  >
                    <TouchAppOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center' }}>
                      {t('play_or_click_timeline_hint')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>

          <SectionCard title={t('creative_summary')}>
            <Typography variant="body2">{data.summary.overall}</Typography>
          </SectionCard>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <SectionCard title={t('motivation_arc')}>
              <Typography variant="body2">{data.summary.motivationArc}</Typography>
            </SectionCard>

            <SectionCard title={t('target_player_profile')}>
              <Typography variant="body2">{data.summary.targetPlayerProfile}</Typography>
            </SectionCard>
          </Box>

          <CreativeAdTagDimensionList tags={data.tags} />
        </Box>
      )}
    </UAInsightsDialog>
  )
}
